// components/ProfileCard.tsx
"use client";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { AdaptiveText, type AdaptiveTextProps } from "./Content/AdaptiveText";
import { WaveCursor } from "./WaveCursor";

export type Person = {
	name: string;
	role: string;
	href?: string;
	imageUrl: string;
	funFact: AdaptiveTextProps["portableText"];
	interests?: string[];
	specialisesIn?: string[];
};

// Terminal text typing animation
const TypingText = ({ text }: { text: string }) => {
	const [displayText, setDisplayText] = useState("");
	const [currentIndex, setCurrentIndex] = useState(0);

	useEffect(() => {
		if (currentIndex < text.length) {
			const timeout = setTimeout(
				() => {
					setDisplayText((prev) => prev + text[currentIndex]);
					setCurrentIndex((prev) => prev + 1);
				},
				30 + Math.random() * 50,
			);

			return () => clearTimeout(timeout);
		}
	}, [currentIndex, text]);

	return (
		<div className="w-full overflow-hidden whitespace-nowrap text-left font-mono text-cyan-300 text-xs">
			<span>{displayText}</span>
			{currentIndex < text.length && (
				<motion.span
					animate={{ opacity: [1, 0, 1] }}
					transition={{ repeat: Number.POSITIVE_INFINITY, duration: 0.8 }}
					className="ml-0.5 inline-block h-3.5 w-1.5 bg-cyan-300 align-middle"
				/>
			)}
		</div>
	);
};

// AI-themed card loading component
const AICardLoading = ({
	isLoading,
	person,
}: { isLoading: boolean; person: Person }) => {
	const [statusPhase, setStatusPhase] = useState(0);
	const terminalRef = useRef<HTMLDivElement>(null);

	const phases = [
		`> Deploying AI agent ${person.name}...`,
		`> Posing as ${person.role}!`,
		"> Guardrails loaded. Evaluating rules...",
		"> Streaming...",
	];

	// biome-ignore lint/correctness/useExhaustiveDependencies: Auto-scroll terminal when new lines are added
	useEffect(() => {
		if (terminalRef.current) {
			terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
		}
	}, [statusPhase]);

	useEffect(() => {
		if (!isLoading) {
			return;
		}

		// Progress through loading phases
		const intervals = [400, 800, 1200]; // Timing for each phase
		const timeouts: NodeJS.Timeout[] = [];

		for (let i = 0; i < intervals.length; i++) {
			const timeout = setTimeout(() => {
				setStatusPhase(i + 1);
			}, intervals[i]);
			timeouts.push(timeout);
		}

		return () => {
			timeouts.forEach(clearTimeout);
		};
	}, [isLoading]);

	// Neural network animation paths
	const neuralPaths = [
		"M5,25 Q15,5 25,25",
		"M5,15 Q25,0 45,15",
		"M25,5 Q35,25 45,5",
		"M15,5 Q25,35 35,5",
	];

	return (
		<motion.div
			className={cn(
				"pointer-events-none absolute inset-0 z-30 flex items-center justify-center overflow-hidden rounded-xl bg-slate-900/90 backdrop-blur-sm",
			)}
			initial={{ opacity: 0 }}
			animate={{ opacity: isLoading ? 1 : 0 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.2 }}
		>
			{/* Neural Network Background Animation */}
			<div className="absolute inset-0 opacity-20">
				<svg className="h-full w-full" viewBox="0 0 50 50">
					{neuralPaths.map((d, i) => (
						<motion.path
							key={i.toString()}
							d={d}
							stroke="rgba(103, 232, 249, 0.8)" // cyan color
							strokeWidth="0.5"
							fill="none"
							initial={{ pathLength: 0, opacity: 0.3 }}
							animate={{
								pathLength: [0, 1, 0],
								opacity: [0.3, 1, 0.3],
							}}
							transition={{
								duration: 2,
								delay: i * 0.2,
								repeat: Number.POSITIVE_INFINITY,
								ease: "easeInOut",
							}}
						/>
					))}
					{/* Neural nodes */}
					{Array.from({ length: 6 }).map((_, i) => (
						<motion.circle
							key={`node-${i.toString()}`}
							cx={10 + (i % 3) * 15}
							cy={10 + Math.floor(i / 3) * 15}
							r="1.5"
							fill="rgba(165, 180, 252, 0.8)" // indigo color
							initial={{ scale: 0.8 }}
							animate={{
								scale: [0.8, 1.5, 0.8],
								opacity: [0.5, 1, 0.5],
							}}
							transition={{
								duration: 2,
								delay: i * 0.3,
								repeat: Number.POSITIVE_INFINITY,
								ease: "easeInOut",
							}}
						/>
					))}
				</svg>
			</div>

			<div className="z-10 flex w-full max-w-[280px] flex-col items-center sm:max-w-[320px]">
				{/* Pulse animation around AI avatar */}
				<div className="relative mb-6 h-20 w-20">
					<div className="h-20 w-20 overflow-hidden rounded-full border-2 border-cyan-500">
						<Image
							src={person.imageUrl}
							alt={person.name}
							width={80}
							height={80}
							className="aspect-square object-cover object-top opacity-80 grayscale"
						/>
						<motion.div
							className="absolute inset-0 bg-gradient-to-b from-indigo-600/10 to-cyan-400/30"
							animate={{ opacity: [0.3, 0.7, 0.3] }}
							transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
						/>
					</div>

					{/* Concentric pulse rings */}
					{Array.from({ length: 3 }).map((_, i) => (
						<motion.div
							key={i.toString()}
							className="absolute inset-0 rounded-full border border-cyan-500"
							initial={{ scale: 1, opacity: 0.8 }}
							animate={{ scale: 1 + (i + 1) * 0.2, opacity: 0 }}
							transition={{
								duration: 2,
								delay: i * 0.3,
								repeat: Number.POSITIVE_INFINITY,
								ease: "easeOut",
							}}
						/>
					))}

					{/* Status indicator */}
					<motion.div
						className="-top-1 -right-1 absolute h-4 w-4 rounded-full bg-gray-500"
						animate={{
							backgroundColor: statusPhase < 3 ? "#6B7280" : "#10B981",
							boxShadow:
								statusPhase < 3
									? "0 0 0 rgba(16, 185, 129, 0)"
									: "0 0 15px rgba(16, 185, 129, 0.7)",
						}}
						transition={{ duration: 0.5 }}
					/>
				</div>

				{/* Terminal window with status updates */}
				<div className="h-[150px] w-full overflow-hidden rounded-md border border-slate-700 bg-[#111827] font-mono shadow-lg">
					{/* Terminal header */}
					<div className="flex items-center gap-2 border-slate-700 border-b bg-[#0f172a] px-3 py-1.5">
						<div className="h-2.5 w-2.5 rounded-full bg-red-500" />
						<div className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
						<div className="h-2.5 w-2.5 rounded-full bg-green-500" />
						<span className="ml-1 text-[10px] text-slate-400">
							neural-terminal
						</span>
					</div>

					{/* Terminal content */}
					<div
						ref={terminalRef}
						className="h-[115px] overflow-y-auto px-3 py-2 text-left"
						style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
					>
						<div className="flex w-full flex-col items-start">
							{phases.slice(0, statusPhase + 1).map((phase, i) => (
								<div
									key={i.toString()}
									className="mb-2.5 flex w-full items-start last:mb-0"
								>
									<span className="mr-2 flex-shrink-0 font-mono text-green-400 text-xs">
										$
									</span>
									{i === statusPhase ? (
										<TypingText text={phase} />
									) : (
										<span className="text-left font-mono text-cyan-300 text-xs">
											{phase}
										</span>
									)}
								</div>
							))}
						</div>
					</div>
				</div>

				{/* Progress bar */}
				<div className="mt-4 h-[40px] w-full">
					<div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
						<motion.div
							className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-indigo-500"
							initial={{ width: "5%" }}
							animate={{ width: `${(statusPhase / 3) * 100}%` }}
							transition={{ duration: 0.5 }}
						/>
					</div>
					<div className="text-center text-[10px] text-cyan-300">
						System Status: {statusPhase < 3 ? "Waking up AI..." : "Ready"}
					</div>
				</div>
			</div>
		</motion.div>
	);
};

export const ProfileCard = ({ person }: { person: Person }) => {
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);

	// Handle click outside to close expanded card
	useEffect(() => {
		if (!expanded) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
				setExpanded(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [expanded]);

	// Handle expand animation with loading state
	const handleExpand = () => {
		if (!expanded) {
			setLoading(true);
			// Simulate AI processing time
			setTimeout(() => {
				setLoading(false);
			}, 2500); // Longer time to see the full loading sequence
		}
		setExpanded(!expanded);
	};

	// Simple view (avatar, name, role only)
	return (
		<div className="relative" ref={cardRef}>
			{/* Simple view is always rendered */}
			<WaveCursor waveText={`Say Hi to ${person.name}!`}>
				<motion.div
					onClick={handleExpand}
					className="relative flex flex-col items-center rounded-md from-slate-100 via-white to-slate-300 p-4 transition-all duration-300 hover:bg-gradient-to-br hover:shadow-sm"
					whileHover={{ scale: 1.03 }}
				>
					<div className="mx-auto mb-2 overflow-hidden rounded-full border-2 border-primary p-0.5 shadow-sm">
						<Image
							className="aspect-square rounded-full object-cover object-top"
							src={person.imageUrl}
							alt={person.name}
							width={80}
							height={80}
						/>
					</div>
					<h3 className="text-center font-medium text-base text-slate-800">
						{person.name}
					</h3>
					<p className="text-center text-slate-500">{person.role}</p>
				</motion.div>
			</WaveCursor>

			{/* Expanded view - absolute positioned and only rendered when expanded */}
			<AnimatePresence>
				{(expanded || loading) && (
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.9 }}
						transition={{ duration: 0.3 }}
						className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex min-h-[450px] w-[90%] min-w-[280px] max-w-md transform flex-col items-center overflow-hidden rounded-xl border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 p-4 text-white shadow-xl sm:min-w-[320px] sm:p-6"
					>
						{/* Content container */}
						<div className="flex w-full flex-col items-center space-y-3 sm:space-y-4">
							{/* Animated background glow effect */}
							<div className="absolute inset-0 h-full w-full overflow-hidden rounded-xl">
								<motion.div
									className="-inset-4 absolute bg-gradient-to-r from-indigo-500 via-cyan-400 to-indigo-500 opacity-20 blur-xl"
									animate={{
										x: ["-25%", "25%", "-25%"],
										y: ["-15%", "15%", "-15%"],
									}}
									transition={{
										repeat: Number.POSITIVE_INFINITY,
										duration: 15,
										ease: "easeInOut",
									}}
									style={{ width: "150%", height: "150%" }}
								/>
							</div>

							{/* Avatar with glowing frame */}
							<div className="relative z-10">
								<motion.div
									initial={{ rotate: -10 }}
									animate={{ rotate: 0 }}
									transition={{ duration: 0.5 }}
									className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-indigo-500 opacity-70 blur-md"
								/>
								<motion.div
									whileHover={{ scale: 1.05 }}
									className="relative overflow-hidden rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 p-1"
								>
									<div className="absolute inset-0 bg-indigo-900/50" />
									<Image
										src={person.imageUrl}
										alt={person.name}
										width={80}
										height={80}
										className="relative z-10 aspect-square rounded-full object-cover object-top sm:h-[100px] sm:w-[100px]"
									/>
									<motion.div
										className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 to-indigo-500/70"
										animate={{
											rotate: [0, 360],
										}}
										transition={{
											repeat: Number.POSITIVE_INFINITY,
											duration: 8,
											ease: "linear",
										}}
									/>
								</motion.div>
							</div>

							{/* Greeting and Name & Role */}
							<div className="relative z-10 w-full space-y-1 text-center sm:space-y-2">
								<motion.div
									initial={{ y: 10, opacity: 0 }}
									animate={{ y: 0, opacity: 1 }}
									transition={{ delay: 0.1 }}
									className="flex items-center justify-center gap-1 sm:gap-2"
								>
									<motion.span
										animate={{ rotate: [0, 20, -20, 10, -10, 0] }}
										transition={{
											repeat: Number.POSITIVE_INFINITY,
											repeatDelay: 1.2,
											duration: 0.8,
											ease: "easeInOut",
										}}
										className="text-lg sm:text-xl"
									>
										👋
									</motion.span>
									<span className="text-cyan-100 text-sm sm:text-base">
										Hey, I am
									</span>
								</motion.div>

								<div>
									<motion.h3
										initial={{ y: 10, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										transition={{ delay: 0.15 }}
										className="bg-gradient-to-r from-cyan-200 to-white bg-clip-text font-bold text-transparent text-xl sm:text-2xl"
									>
										{person.name}
									</motion.h3>
									<motion.p
										initial={{ y: 10, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										transition={{ delay: 0.2 }}
										className="text-cyan-100 text-sm opacity-80 sm:text-base"
									>
										{person.role}
									</motion.p>

									{person.href && (
										<motion.a
											href={person.href}
											target="_blank"
											rel="noopener noreferrer"
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											transition={{ delay: 0.25 }}
											className="group mt-1 inline-flex items-center text-cyan-300 text-sm transition-colors hover:text-cyan-100 sm:mt-2"
											onClick={(e) => e.stopPropagation()}
										>
											View Profile
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="ml-1 h-2 w-2 transform transition-transform group-hover:translate-x-1 sm:h-3 sm:w-3"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M9 5l7 7-7 7"
												/>
											</svg>
										</motion.a>
									)}
								</div>
							</div>

							<Separator className="relative z-10 w-full bg-cyan-800/50" />

							{/* Fun Fact */}
							<motion.div
								initial={{ y: 20, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								transition={{ delay: 0.3 }}
								className="relative z-10 w-full px-1 text-center sm:px-0"
							>
								<AdaptiveText
									portableText={person.funFact}
									className="!text-base sm:!text-lg m-0 mx-auto text-white"
								/>
							</motion.div>

							{/* Interests - Only show if interests exist and are not empty */}
							{person.interests && person.interests.length > 0 && (
								<>
									<Separator className="relative z-10 w-full bg-cyan-800/50" />
									<motion.div
										initial={{ y: 20, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										transition={{ delay: 0.4 }}
										className="relative z-10 w-full"
									>
										<h4 className="mb-1 text-center font-medium text-cyan-200 text-sm sm:mb-2 sm:text-base">
											Interests
										</h4>
										<div className="flex flex-wrap justify-center gap-1 sm:gap-2">
											<AnimatePresence>
												{person.interests.map((interest, index) => (
													<motion.div
														key={interest}
														initial={{ opacity: 0, scale: 0.8 }}
														animate={{ opacity: 1, scale: 1 }}
														transition={{ delay: 0.4 + index * 0.1 }}
													>
														<Badge className="border-slate-600 bg-slate-700/80 text-sm text-white transition-colors hover:bg-slate-600">
															{interest}
														</Badge>
													</motion.div>
												))}
											</AnimatePresence>
										</div>
									</motion.div>
								</>
							)}

							{/* Specialises In - Only show if specialisesIn exist and are not empty */}
							{person.specialisesIn && person.specialisesIn.length > 0 && (
								<>
									<Separator className="relative z-10 w-full bg-cyan-800/50" />
									<motion.div
										initial={{ y: 20, opacity: 0 }}
										animate={{ y: 0, opacity: 1 }}
										transition={{ delay: 0.5 }}
										className="relative z-10 w-full"
									>
										<h4 className="mb-1 text-center font-medium text-cyan-200 text-sm sm:mb-2 sm:text-base">
											Specialises In
										</h4>
										<div className="flex flex-wrap justify-center gap-1 sm:gap-2">
											<AnimatePresence>
												{person.specialisesIn.map((specialty, index) => (
													<motion.div
														key={specialty}
														initial={{ opacity: 0, scale: 0.8 }}
														animate={{ opacity: 1, scale: 1 }}
														transition={{ delay: 0.5 + index * 0.1 }}
													>
														<Badge className="border-indigo-600 bg-indigo-800/90 text-sm text-white transition-colors hover:bg-indigo-700">
															{specialty}
														</Badge>
													</motion.div>
												))}
											</AnimatePresence>
										</div>
									</motion.div>
								</>
							)}
						</div>

						{/* AI Loading overlay - absolute positioned on top of content */}
						<div className="absolute inset-0 z-[60]">
							<AICardLoading isLoading={loading} person={person} />
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
